import { appConfig } from '@src/config/appConfig';
import { stickerImageExtensionsAllowed } from '@src/utils/mediaUtils';
import Picker, {
  Categories,
  EmojiClickData,
  EmojiStyle,
  Theme,
} from 'emoji-picker-react';
import { useThemeMode } from '@src/contexts/ThemeMode';
import {
  ChangeEvent,
  MutableRefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  AddOwnStickersButtonId,
  CustomEmojisList,
  EmojisCategories,
  StickersKeyName,
} from './constants';
import './index.css';
import {
  fileToBase64,
  filterSuggestedItems,
  getCustomStickerArr,
  urlToObject,
  validateFile,
} from './utils';

type StickerData = {
  names: string[];
  imgUrl: string;
  id: string;
};

interface ICustomEmojiPicker {
  handleAppendEmoticon: (emoji: string) => void;
  handleSendSticker: (file: File) => void;
  withUploadOption?: boolean;
  withStickersOption?: boolean;
}
const CustomEmojiPicker: React.FC<ICustomEmojiPicker> = ({
  handleSendSticker,
  handleAppendEmoticon,
  withUploadOption = false,
  withStickersOption = false,
}) => {
  const { mode } = useThemeMode();
  const [customEmojisArr, setCustomEmojisArr] = useState(CustomEmojisList);
  const [open, setOpen] = useState(true);

  const stickerFileInput = useRef() as MutableRefObject<HTMLInputElement>;

  const handleSentEmoji = async (emojiObj: EmojiClickData) => {
    filterSuggestedItems();
    if (emojiObj.emoji.startsWith(AddOwnStickersButtonId)) {
      stickerFileInput.current?.click();
      return;
    }

    const file = await urlToObject(emojiObj);
    if (emojiObj.isCustom) {
      if (file instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(`Error on: handleSendSticker: ${file.message}`);
      } else {
        handleSendSticker(file);
      }
    } else handleAppendEmoticon(emojiObj.emoji);
    setOpen(false);
  };

  const handleUploadStickers = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target?.files) {
      return;
    }
    const files = Array.from(event.target.files).map(async file => {
      const val = await validateFile(file);
      if (val) return file;
      return null;
    });

    const res = await Promise.all(files);
    const filteredFiles = res
      .map(file => {
        if (file)
          return new File([file], file.name.toLocaleLowerCase(), {
            type: file.type,
          });
        return null;
      })
      .filter(file => file !== null);

    const stickersArr: string[] = appConfig.getData(StickersKeyName) || [];

    const uploadedStickersArr = filteredFiles.map(async sticker => {
      if (sticker === null) return null;
      const data = await fileToBase64(sticker);
      const url = URL.createObjectURL(sticker);
      appConfig.setData(url, data);

      const stickerName =
        sticker.name.split('.').at(0) || `${sticker}-${url}.webp`;

      return {
        names: [stickerName],
        imgUrl: url,
        id: url,
      } as StickerData;
    });

    const arr = await Promise.all(uploadedStickersArr);

    const newStickersArr = [
      ...stickersArr,
      ...arr.filter(sticker => sticker !== null),
    ];
    appConfig.setData(StickersKeyName, newStickersArr);
    updateCustomEmojisArr();
  };

  const updateCustomEmojisArr = () => {
    const button = CustomEmojisList[0];
    const arr = getCustomStickerArr()
      .reverse()
      .map((sticker: StickerData) => {
        const blob = appConfig.getData(sticker.id);

        return {
          ...sticker,
          imgUrl: blob,
        };
      });

    setCustomEmojisArr([button, ...arr, ...CustomEmojisList.slice(1)]);
  };

  useEffect(() => {
    filterSuggestedItems();
    updateCustomEmojisArr();
  }, []);

  return (
    <>
      {withUploadOption && (
        <input
          type="file"
          id="inStickerUpload"
          name="stickerUpload"
          style={{ display: 'none' }}
          onChange={handleUploadStickers}
          ref={stickerFileInput}
          accept={[...stickerImageExtensionsAllowed].join(',')}
          multiple
        />
      )}
      <Picker
        open={open}
        theme={mode === 'dark' ? Theme.DARK : Theme.LIGHT}
        lazyLoadEmojis
        customEmojis={customEmojisArr}
        onEmojiClick={emojiObj => {
          handleSentEmoji(emojiObj);
        }}
        skinTonesDisabled
        previewConfig={{
          showPreview: false,
        }}
        emojiStyle={EmojiStyle.APPLE}
        categories={
          withStickersOption
            ? EmojisCategories
            : EmojisCategories.filter(
                item => item.category !== Categories.CUSTOM
              )
        }
      />
    </>
  );
};

export default CustomEmojiPicker;
